<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :embedded="embedded"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <div class="row no-gutters h-100">
      <div class="col-2 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Production Stage Task Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyStageTaskFilter"
              @reset="resetStageTaskFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingStageTaskFilter.startDate"
                  :endDate="editingStageTaskFilter.endDate"
                  @updateStartDate="editingStageTaskFilter.startDate = $event"
                  @updateEndDate="editingStageTaskFilter.endDate = $event"
                />
                <FormHorizontalRule />
                Production Stage
                <FormSelect :options="productionStageLookup" v-model="editingStageTaskFilter.productionStageId" />

              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedStageTaskFilter.startDate | formatDate}} - {{appliedStageTaskFilter.endDate | formatDate}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Production Stage" highlight>
              {{stageTaskSummaryData.productionStageName}}
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Total Completed Units">
              {{formatDecimal(stageTaskSummaryData.totalCompletedUnits, 2, true)}} <span v-html="stageTaskSummaryData.uomSuffix" />
              <template v-slot:info>Across all stations within the date range</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        
      </div>
      <div class="col-10">
        <ChartBar
          title="Monthly Completed Units"
          :series="stageTaskMonthlyData.series"
          :xAxis="stageTaskMonthlyData.xAxis"
          :yAxis="stageTaskMonthlyData.yAxis"
          :legend="stageTaskMonthlyData.legend"
          :options="stageTaskMonthlyData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartBar from '@/components/charts/chartBar'

import { UOMTypeUnitsSafeLookup, UOMTypeUnitsLookup } from '@/store/types'

export default {
  name: 'ProductionStageMonthlyUnitsChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      stageTaskSummaryData: {
        productionStageName: '',
        uomType: '',
        totalCompletedUnits: 0
      },
      stageTaskMonthlyData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultStageTaskFilter: {    
        startDate: moment().local().subtract(90, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        productionStageId: null
      },
      editingStageTaskFilter: {},
      appliedStageTaskFilter: {},

      productionStageLookup: []
    }
  },
  computed: {
  },
  mounted() {
    this.editingStageTaskFilter = _.clone(this.defaultStageTaskFilter)
    this.appliedStageTaskFilter = _.clone(this.defaultStageTaskFilter)

    // Note: Only load chart data when we have a production stage set
    this.requestData()

    this.subscribeEndpoint('/production/sheetschedule', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/production/sheetschedule')
  },
  watch: {
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    applyStageTaskFilter() {
      this.appliedStageTaskFilter = _.clone(this.editingStageTaskFilter)

      this.requestChartData()
    },
    resetStageTaskFilter() {
      this.editingStageTaskFilter = _.clone(this.defaultStageTaskFilter)
      this.appliedStageTaskFilter = _.clone(this.defaultStageTaskFilter)

      this.requestChartData()
    },
    requestData() {
      // Load the production stages
      this.$apiService.productionService.getProductionStageList()
      .then(response => {
        this.productionStageLookup = response.data
        .filter(productionStage => productionStage.isActive)
        .map(productionStage => {
          return {
            value: productionStage.id,
            name: productionStage.name
          }
        })

        // Apply the 1st stage to the default filter
        if(this.productionStageLookup.length > 0) {
          this.defaultStageTaskFilter.productionStageId = this.productionStageLookup[0].value
          this.resetStageTaskFilter()
        }
      })
      .catch(err => {
        this.displayNetworkError(err, 'Could not load data')
      })
    },
    requestChartData() {
      var stageTaskFilter = {
        startDate: this.appliedStageTaskFilter.startDate,
        endDate: this.appliedStageTaskFilter.endDate,
        productionStageId: this.appliedStageTaskFilter.productionStageId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null,
      }

      this.stageTaskMonthlyData.options = {
        aria: {
          enabled: true,
          decal: {
            show: true
          }
        }
      }

      let widgetComp = this

      this.$apiService.analyticsService.getProductionStageTasksCompletedChartData(stageTaskFilter)
      .then(response => {
        let chartData = response.data

        this.stageTaskSummaryData.productionStageName = this.formatLookupName(stageTaskFilter.productionStageId, this.productionStageLookup)

        var uomType = ''
        if(chartData.length > 0)
          uomType = chartData[0].uomType

        var uomSuffix = this.formatLookupName(uomType, UOMTypeUnitsSafeLookup)
        
        this.stageTaskSummaryData.uomType = uomType
        this.stageTaskSummaryData.uomSuffix = uomSuffix
        this.stageTaskSummaryData.totalCompletedUnits = chartData.reduce((cur, item) => cur + item.totalCompletedUnits, 0)

        // Update date string
        chartData.forEach(dataEntry => {
          dataEntry.dateStr = moment(dataEntry.date).format('MMM \'YY')
        })

        this.stageTaskMonthlyData.legend = {}
        this.stageTaskMonthlyData.xAxis = {
          exportName: 'Date',
          exportData: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM YYYY')),
          data: _.uniq(chartData.map(chartEntry => chartEntry.dateStr))
        }
        this.stageTaskMonthlyData.yAxis = [
          {
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                return `${value} ${uomSuffix}`
              }
            }
          }
        ]

        var stationIdList = _.uniq(chartData.map(dataEntry => dataEntry.productionStationId))

        this.stageTaskMonthlyData.series = []
        stationIdList.forEach(stationId => {
          var stationDataList = chartData.filter(dataEntry => dataEntry.productionStationId == stationId)

          if(stationDataList.length > 0) {
            this.stageTaskMonthlyData.series.push({
              type: 'bar',
              name: stationDataList[0].productionStationName,
              stack: `station-${stationId}`,
              emphasis: {
                focus: 'series'
              },
              tooltip: {
                valueFormatter(value) {
                  return widgetComp.formatDecimal(value, 2) + ` ${uomSuffix}`
                }
              },
              data: stationDataList.map(chartEntry => {
                return [chartEntry.dateStr, chartEntry.totalCompletedUnits]
              })
            })
          }
        })
      })
    },
  }
}
</script>